import { Form } from "@/components/ui/form";
import FormSection from "../../FormSection/FormSection";
import { Button } from "@components/ui/button";
import FormFieldInput from "../../FormField/FormFieldInput";
import { CreateProcedureFormValuesType, createProcedureFormSchema } from "../schemas.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import FormFieldDatePicker from "../../FormField/FormFieldDatePicker";
import FormFieldSelect from "../../FormField/FormFieldSelect";

const typeOfLetter2 = [
  "2.1 wniosek do projektu aktu",
  "2.2 uwaga do konsultowanego projektu aktu",
  "2.3 wniosek o zmianę aktu",
  "2.4 wniosek o sporządzenie aktu",
];
const spatialPlanningActType2 = [
  "3.1 plan ogólny gminy",
  "3.2 miejscowy plan zagospodarowania przestrzennego, w tym zintegrowany plan inwestycyjny lub miejscowy plan rewitalizacji",
  "3.3 uchwała ustalająca zasady i warunki sytuowania obiektów małej architektury, tablic reklamowych i urządzeń reklamowych oraz ogrodzeń, ich gabaryty, standardy jakościowe oraz rodzaje materiałów budowlanych, z jakich mogą być wykonane",
  "3.4 audyt krajobrazowy",
  "3.5 plan zagospodarowania przestrzennego województwa",
];
type CreateProcedureFormPropsType = {
  onSubmit: (values: CreateProcedureFormValuesType) => void;
  defaultValues?: Partial<CreateProcedureFormValuesType>;
  /** Czy slug (pole "Nazwa URL") ma być zablokowany do zmiany — używane przy edycji. */
  lockSlug?: boolean;
  /** Tytuł sekcji formularza. Default "Nowa procedura planistyczna". */
  title?: string;
  /** Tekst przycisku submit. Default "Utwórz". */
  submitLabel?: string;
  /** Ukryj wbudowany przycisk submit — przydatne gdy edytor dokłada własny
   *  przycisk zewnętrzny (np. na samym dole pod sekcjami plików). Zewnętrzny
   *  przycisk używa atrybutu form="<formId>" żeby submitować ten formularz. */
  hideSubmit?: boolean;
  /** ID formularza — pozwala zewnętrznemu przyciskowi submitować przez form=. */
  formId?: string;
};

function CreateProcedureForm({
  onSubmit,
  defaultValues,
  lockSlug = false,
  title = "Nowa procedura planistyczna",
  submitLabel = "Utwórz",
  hideSubmit = false,
  formId,
}: CreateProcedureFormPropsType) {
  const form = useForm<CreateProcedureFormValuesType>({
    resolver: zodResolver(createProcedureFormSchema),
    defaultValues: {
      name: defaultValues?.name ?? "",
      displayName: defaultValues?.displayName ?? "",
      description: defaultValues?.description ?? "",
      activeFrom: defaultValues?.activeFrom
        ? new Date(defaultValues.activeFrom)
        : new Date(),
      activeTo: defaultValues?.activeTo
        ? new Date(defaultValues.activeTo)
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      applicationPlace: defaultValues?.applicationPlace ?? "",
      typeOfLetter: defaultValues?.typeOfLetter ?? "",
      spatialPlanningActType: defaultValues?.spatialPlanningActType ?? "",
      files: undefined,
      spatialPlanningActName: defaultValues?.spatialPlanningActName ?? "",
      municipalitysGeneralPlanOrLocalDevelopmentPlan:
        defaultValues?.municipalitysGeneralPlanOrLocalDevelopmentPlan ?? "",
      mapInitialLat: defaultValues?.mapInitialLat ?? ("" as any),
      mapInitialLng: defaultValues?.mapInitialLng ?? ("" as any),
      mapInitialZoom: defaultValues?.mapInitialZoom ?? ("" as any),
    },
  });

  /** Pobiera centrum miasta/gminy z Nominatim (OpenStreetMap) i wpisuje
   *  do pól mapInitialLat/Lng. Używa wartości z pola "Nazwa procedury"
   *  jako zapytania (najczęściej „Plan ogólny <miasta>") — user może też
   *  wpisać dowolną miejscowość w prompt'ie. */
  const handleLookupCenter = async () => {
    const promptText = window.prompt(
      'Podaj nazwę miasta lub gminy do wyszukania centrum (np. „Nowy Sącz", „Gmina Kraków"):',
      form.getValues("displayName") || "",
    );
    if (!promptText) return;
    try {
      const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
        promptText,
      )}&format=jsonv2&countrycodes=pl&limit=1`;
      const resp = await fetch(url);
      const data = await resp.json();
      if (!Array.isArray(data) || data.length === 0) {
        alert(`Nie znaleziono: ${promptText}`);
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      form.setValue("mapInitialLat", lat as any, { shouldDirty: true });
      form.setValue("mapInitialLng", lng as any, { shouldDirty: true });
      if (!form.getValues("mapInitialZoom")) {
        form.setValue("mapInitialZoom", 13 as any, { shouldDirty: true });
      }
    } catch (err) {
      alert("Błąd wyszukiwania: " + (err as Error).message);
    }
  };
  return (
    <Form {...form}>
      <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
        <FormSection title={title}>
          <FormFieldInput
            form={form}
            description={
              lockSlug
                ? "Nazwa w adresie URL (slug) — nie można zmienić po utworzeniu procedury."
                : "Nazwa w adresie URL (slug). Strona procedury będzie dostępna pod /{id_gminy}/{ta_nazwa}. Dozwolone: litery bez polskich znaków (A-Z, a-z), cyfry, podkreślenie (_) i myślnik (-). Np. PlanOgolny albo plan-ogolny-2026."
            }
            placeholder="np. PlanOgolny"
            label="Nazwa URL"
            name="name"
            disabled={lockSlug}
          />
          <FormFieldInput
            form={form}
            description="Pełna nazwa wyświetlana mieszkańcom"
            placeholder="np. Plan Ogólny Gminy 2026"
            label="Nazwa procedury"
            name="displayName"
          />
          <FormFieldInput
            form={form}
            description="Opis procedury widoczny dla mieszkańców"
            placeholder="Opis procedury..."
            label="Opis"
            name="description"
          />
          <div className="grid grid-cols-2 gap-4">
            <FormFieldDatePicker form={form as any} name="activeFrom" label="Przyjmowanie wniosków od" />
            <FormFieldDatePicker form={form as any} name="activeTo" label="Przyjmowanie wniosków do" />
          </div>
          <FormFieldInput
            form={form}
            placeholder="np. Wójt Gminy / Burmistrz / Prezydent Miasta"
            label="Organ, do którego jest składane pismo"
            name="applicationPlace"
          />
          <FormFieldSelect
            form={form}
            placeholder="Wybierz rodzaj aktu"
            label="Rodzaj aktu planowania przestrzennego"
            name="spatialPlanningActType"
            options={spatialPlanningActType2.map((v) => ({ label: v, value: v }))}
          />
          <FormFieldSelect
            form={form}
            placeholder="Wybierz rodzaj pisma"
            label="Rodzaj pisma"
            name="typeOfLetter"
            options={typeOfLetter2.map((v) => ({ label: v, value: v }))}
          />
          <FormFieldInput
            form={form}
            placeholder="np. Uchwała nr X Rady Gminy z dnia..."
            label="Nazwa aktu planowania przestrzennego (7.2.1)"
            name="spatialPlanningActName"
          />
          <FormFieldInput
            form={form}
            placeholder="np. Plan ogólny gminy XYZ"
            label="Nazwa planu ogólnego gminy lub MPZP (7.3.1)"
            name="municipalitysGeneralPlanOrLocalDevelopmentPlan"
          />
          <FormFieldInput form={form} placeholder="Wybierz pliki" label="Załączniki (dokumenty, mapy)" name="files" inputType="file" multiple />

          {/* Punkt startowy mapy dla tej procedury */}
          <div className="mt-4 p-4 border border-gray-200 rounded-md bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Punkt startowy mapy</h4>
              <button
                type="button"
                onClick={handleLookupCenter}
                className="text-xs px-3 py-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400">
                🔍 Znajdź centrum miasta (Nominatim)
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Opcjonalne — szerokość, długość geograficzna i zoom na starcie mapy dla tej procedury. Gdy puste, mapa
              użyje domyślnego punktu (np. centrum Polski). Przycisk wyszukuje automatycznie w OpenStreetMap.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <FormFieldInput
                form={form}
                placeholder="49.62"
                label="Szerokość (lat)"
                name="mapInitialLat"
                inputType="number"
              />
              <FormFieldInput
                form={form}
                placeholder="20.69"
                label="Długość (lng)"
                name="mapInitialLng"
                inputType="number"
              />
              <FormFieldInput
                form={form}
                placeholder="13"
                label="Zoom (0–22)"
                name="mapInitialZoom"
                inputType="number"
              />
            </div>
          </div>
        </FormSection>
        {!hideSubmit && <Button type="submit" className="mt-4">{submitLabel}</Button>}
      </form>
    </Form>
  );
}

export default CreateProcedureForm;
